import SingleTag from "@/components/Tags/tag.component";
import { PROJECT_CATEGORY_ID } from "@/constants/utils.constants";
import { useTags } from "@/hooks/AppContext/tags.hook";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { NewTaskTag, Tag } from "@/types";
import React, { useMemo } from "react";

type TaskCellTagsNotEditingProps = {
  taskTags: Tag[];
  selectedEditableTaskTags?: NewTaskTag[];
};

const TaskCellTagsNotEditing: React.FC<TaskCellTagsNotEditingProps> = ({
  taskTags,
  selectedEditableTaskTags,
}) => {
  const { findProjectTags, findNonProjectTags } = useTags();

  const selectedEditableTaskTagsProject = useMemo(() => {
    return selectedEditableTaskTags?.filter(
      (tag) => tag.category_id === PROJECT_CATEGORY_ID
    );
  }, [selectedEditableTaskTags]);

  const taskTagsProject = useMemo(() => {
    return findProjectTags(taskTags);
  }, [taskTags, findProjectTags]);

  const selectedEditableTaskTagsNonProject = useMemo(() => {
    return selectedEditableTaskTags?.filter(
      (tag) => tag.category_id !== PROJECT_CATEGORY_ID
    );
  }, [selectedEditableTaskTags]);

  const taskTagsNonProject = useMemo(() => {
    return findNonProjectTags(taskTags);
  }, [taskTags, findNonProjectTags]);

  return (
    <div className="grid grid-cols-2 items-center">
      <div className="px-2 col-span-1 w-full truncate select-none">
        <TooltipProvider>
          <Tooltip delayDuration={150}>
            <TooltipTrigger>
              <div className="w-full flex">
                {/* Tags inline */}
                {selectedEditableTaskTagsProject ? (
                  <>
                    {selectedEditableTaskTagsProject.slice(0, 1).map((tag) => (
                      <SingleTag
                        key={tag.tag + tag.category_id}
                        id={tag.tag + tag.category_id}
                        name={tag.tag}
                        color={tag.color}
                        full={false}
                      />
                    ))}
                    {selectedEditableTaskTagsProject.length > 1 && (
                      <span className="ms-1 truncate max-w-full">...</span>
                    )}
                  </>
                ) : (
                  <>
                    {taskTagsProject.slice(0, 1).map((tag) => (
                      <SingleTag
                        key={tag.id}
                        id={tag.id}
                        name={tag.name}
                        color={tag.color}
                        full={false}
                      />
                    ))}
                    {taskTagsProject.length > 1 && (
                      <span className="ms-1 truncate max-w-full">...</span>
                    )}
                  </>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="bg-accent dark:bg-accent outline outline-1 outline-white dark:outline-1 dark:outline dark:outline-neutral-700/35"
              align="start"
            >
              {/* Tags in Tooltip */}
              {selectedEditableTaskTagsProject
                ? selectedEditableTaskTagsProject.map((tag) => (
                    <div key={tag.tag + tag.category_id} className="pb-1">
                      <SingleTag
                        id={tag.tag + tag.category_id}
                        name={tag.tag}
                        color={tag.color}
                        full={true}
                      />
                    </div>
                  ))
                : // Tags in Tooltip
                  taskTagsProject.map((tag) => (
                    <div key={tag.id} className="pb-1">
                      <SingleTag
                        id={tag.id}
                        name={tag.name}
                        color={tag.color}
                        full={true}
                      />
                    </div>
                  ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="px-2 col-span-1 w-full truncate select-none">
        <TooltipProvider>
          <Tooltip delayDuration={150}>
            <TooltipTrigger>
              <div className="w-full flex">
                {/* Tags inline */}
                {selectedEditableTaskTagsNonProject ? (
                  <>
                    {selectedEditableTaskTagsNonProject
                      .slice(0, 1)
                      .map((tag) => (
                        <SingleTag
                          key={tag.tag + tag.category_id}
                          id={tag.tag + tag.category_id}
                          name={tag.tag}
                          color={tag.color}
                          full={false}
                        />
                      ))}
                    {selectedEditableTaskTagsNonProject.length > 1 && (
                      <span className="ms-1 truncate max-w-full">...</span>
                    )}
                  </>
                ) : (
                  <>
                    {taskTagsNonProject.slice(0, 1).map((tag) => (
                      <SingleTag
                        key={tag.id}
                        id={tag.id}
                        name={tag.name}
                        color={tag.color}
                        full={false}
                      />
                    ))}
                    {taskTagsNonProject.length > 1 && (
                      <span className="ms-1 truncate max-w-full">...</span>
                    )}
                  </>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="bg-accent dark:bg-accent outline outline-1 outline-white dark:outline-1 dark:outline dark:outline-neutral-700/35"
              align="start"
            >
              {/* Tags in Tooltip */}
              {selectedEditableTaskTagsNonProject
                ? selectedEditableTaskTagsNonProject.map((tag) => (
                    <div key={tag.tag + tag.category_id} className="pb-1">
                      <SingleTag
                        id={tag.tag + tag.category_id}
                        name={tag.tag}
                        color={tag.color}
                        full={true}
                      />
                    </div>
                  ))
                : // Tags in Tooltip
                  taskTagsNonProject.map((tag) => (
                    <div key={tag.id} className="pb-1">
                      <SingleTag
                        id={tag.id}
                        name={tag.name}
                        color={tag.color}
                        full={true}
                      />
                    </div>
                  ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default TaskCellTagsNotEditing;
